<template>
  <DetailCard :loading="loading">
    <template #heading>
      <span class="pi pi-exclamation-triangle"></span>
      <a :href="planningsExternalUrl" target="_blank">
        {{ $t(`messages.detail.plannings.title`) }}
      </a>
    </template>
    <template #controls>
      <MapHelpTooltip size="sm" variant="secondary" />
    </template>
    <div id="detail-plannings-map" class="plannings-map">
      <MapComponent
        :center="mapview.center"
        :extent="mapview.extent"
        :zoom="mapview.zoom"
        :bbox="mapview.bbox"
        :minZoom="mapview.minZoom"
        :maxZoom="mapview.maxZoom"
        :projection="mapview.projection"
        :baseLayers="mapview.baseLayers"
        :infoLayers="mapview.infoLayers"
        :overlay="mapview.overlay"
        :zoomControl="true"
        :twoFingerPanZoom="true"
        @update="exportMap"
        @ready="onMapReady"
        ref="map"
      />
      <PlanningLegendOverlay :legend="planningLegend" />
    </div>
    <DetailPlanningInfoComponent />
    <div class="p-4">
      <label for="detailplaneering" class="font-bold block mb-2">
        {{ $t(`messages.detail.landboard.detailplaneering`) }}
      </label>
      <RichTextEditor
        v-model="planningDoc"
        :object-id="objectId"
        category="landboard"
        name="detailplaneering"
        :min-height="160"
        @update:model-value="saveDoc"
      >
        <template #toolbar-extra>
          <button
            v-if="draftHoone"
            type="button"
            class="toolbar-btn rte-tt"
            :disabled="ehrDraftLoading"
            :aria-label="$t('messages.editor.ehrDraft')"
            :data-tt="$t('messages.editor.ehrDraft')"
            @click="fillFromEhr"
          >
            <span v-if="!ehrDraftLoading" class="pi pi-sparkles text-sm"></span>
            <span v-else class="pi pi-spinner pi-spin text-sm"></span>
          </button>
        </template>
      </RichTextEditor>
    </div>
  </DetailCard>
</template>

<script setup>
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DetailCard from '@/components/detail/DetailCard.vue'
import DetailPlanningInfoComponent from '@/components/detail/data/DetailPlanningInfoComponent.vue'
import RichTextEditor from '@/components/editor/RichTextEditor.vue'
import MapComponent from '@/components/map/MapComponent.vue'
import MapHelpTooltip from '@/components/map/MapHelpTooltip.vue'
import PlanningLegendOverlay from '@/components/detail/data/PlanningLegendOverlay.vue'
import { PLANNING_LEGEND_RENDER_SCALE } from '@/constants'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_PLANNINGS_MAP_FEATURE_LAYER,
  DETAIL_PLANNINGS_MAP_INFO_LAYERS,
  DETAIL_PLANNINGS_OVERLAY_LAYER_IDS,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import { resetTileRetry, TILE_LOAD_FAILED_KEY } from '@/services/map/LayerService'
import { transform } from '@/services/map/ProjectionService'
import { buildPlanningEhrDraft, isBlankPlanningDoc } from '@/services/planningEhrDraft'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'

defineProps({
  objectId: { type: [String, Number], default: '' },
})

const store = useDetailStore()
const evaluationStore = useEvaluationStore()

const { cadasterId, landBoard, planningInfo } = storeToRefs(store)

// RED-268: the rich Tiptap doc (landboard.detailplaneering_doc) is
// INDEPENDENT of the short landboard.detailplaneering string — the doc feeds
// the ${detailplaneering} report section, the string stays the kohandustabel
// `ehitusoigus` master-cell value (RED-267) and is no longer edited here.
const planningDoc = ref(null)

const saveDoc = doc => {
  landBoard.value.detailplaneering_doc = doc
  evaluationStore.save({ landboard: { detailplaneering_doc: doc } })
}

onMounted(() => {
  // RED-299: the editor is no longer land-only — built-property evaluations
  // share the same landboard.detailplaneering_doc storage and export codes.
  planningDoc.value = landBoard.value?.detailplaneering_doc ?? null

  // RED-293: üldplaneering name / date / juhtotstarve below the map.
  store.fetchPlanningInfo(cadasterId.value)
})

// RED-268 EHR auto-draft: a planned/under-construction hoone on the parcel
// carries the ehitusõigus evidence (ehitusluba, spec figures) — draft the
// section from it instead of retyping the register by hand.
const { t } = useI18n()
const toast = useToast()
const transactionStore = useTransactionStore()

const ehrDraftLoading = shallowRef(false)

const draftHoone = computed(() =>
  (store.buildings || []).find(b => b.type === 'Hoone' && b.ehrCode),
)

const fillFromEhr = async () => {
  const hoone = draftHoone.value
  if (!hoone || ehrDraftLoading.value) {
    return
  }

  ehrDraftLoading.value = true
  try {
    const [detail, docs] = await Promise.all([
      axios.get('/xroad/ehr/' + hoone.ehrCode),
      axios.get('/xroad/documents/' + hoone.ehrCode),
    ])

    const draft = buildPlanningEhrDraft({
      building: hoone,
      detail: detail.data,
      documents: docs.data?.everything || [],
    })
    if (!draft) {
      toast.add({ severity: 'warn', summary: t('messages.editor.ehrDraftEmpty'), life: 4000 })
      return
    }

    // Append to existing content, replace only a blank doc — never destroy.
    const existing = isBlankPlanningDoc(planningDoc.value) ? [] : planningDoc.value.content
    const doc = { type: 'doc', content: [...existing, ...draft.content] }
    planningDoc.value = doc
    saveDoc(doc)

    // Independent values (RED-267/268): seed the short kohandustabel
    // ehitusoigus string only when it is still empty.
    if (draft.shortValue && !landBoard.value?.detailplaneering) {
      landBoard.value.detailplaneering = draft.shortValue
      evaluationStore.save({ landboard: { detailplaneering: draft.shortValue } })
      if (transactionStore.masterData) {
        transactionStore.masterData.ehitusoigus = draft.shortValue
      }
    }

    toast.add({ severity: 'success', summary: t('messages.editor.ehrDraftDone'), life: 3000 })
  } catch {
    toast.add({ severity: 'error', summary: t('messages.editor.ehrDraftFailed'), life: 5000 })
  } finally {
    ehrDraftLoading.value = false
  }
}

const { findLayer } = useDetailMapHelpers()

const map = ref(null)
const loading = shallowRef(false)

const mapview = ref({
  status: true,
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
  zoom: 3,
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  minZoom: MAP_MINZOOM,
  maxZoom: MAP_MAXZOOM,
  projection: MAP_PROJECTION,
  baseLayers: [findLayer('ma-bw')],
  // The tier overlays start hidden: applyOverlay() switches on the one the
  // backend picked, on the url it resolved. Created visible, all three would
  // request tiles on stale fallback ids before the payload lands, and a dead
  // id among them raised the map-wide failure notice (Tartu, 2026-08-28).
  infoLayers: DETAIL_PLANNINGS_MAP_INFO_LAYERS.map(layer =>
    findLayer(layer, !DETAIL_PLANNINGS_OVERLAY_LAYER_IDS.includes(layer)),
  ),
  overlay: null,
  // Seeded so MapComponent fits the parcel before its first rendered frame
  // (see DetailRestrictionsComponent).
  bbox: landBoard.value?.bbox ?? null,
})

const planningsExternalUrl = computed(() => {
  if (cadasterId.value) {
    return (
      'https://xgis.maaamet.ee/xgis2/page/app/planeeringud?searchid=FUUKAT101x2&TUNNUS=' +
      cadasterId.value +
      '&hide=true'
    )
  }
  return '#'
})

const createMapFeatures = geometry => {
  const layer = findLayer(DETAIL_PLANNINGS_MAP_FEATURE_LAYER)
  layer.features.push(createGeoJsonFeature(geometry, {}))
  mapview.value.overlay = layer
}

// The docx ${kaart-planeeringud} is THIS capture (RED-293). It runs on map
// ready and on every moveend, and — the part the first rounds missed — after
// every applyOverlay(), because the maakasutus tier is switched on seconds
// after the first capture and a frame taken before it has no colours at all.
// Generations keep the export honest: a capture belongs to the overlay
// generation it started under, an older capture never overwrites a newer
// one, and a capture that finished while tiles were still loading (the 8 s
// ceiling in waitForMapLoaded, or the tile-retry ladder, which the map's
// load events do not see) is re-taken on the next loadend / tileloadend.
let mapLoading = false
let recaptureOnLoadEnd = false

const exportMap = async () => {
  if (!map.value) {
    return
  }
  const generation = store.planningMapGeneration
  // A map without a size (the card is on the hidden Vara andmed tab) never
  // reaches rendercomplete; waiting 8 s for it would only delay the export,
  // which gets no plannings map from a hidden card either way.
  if (!map.value.map?.getSize()?.[0]) {
    store.planningCaptureGeneration = Math.max(store.planningCaptureGeneration, generation)
    return
  }
  const mapExport = await map.value.exportMap()
  if (!map.value || generation < store.planningCaptureGeneration) {
    return
  }
  store.setMapExport({ key: 'plannings', value: mapExport })

  // Remember the captured viewport (EPSG:3301) so the docx maakasutus legend
  // can be scoped to the categories visible in this exact excerpt (RED-293).
  const olMap = map.value.map
  const size = olMap?.getSize()
  if (size?.[0] && size?.[1]) {
    store.planningMapView = {
      bbox: olMap.getView().calculateExtent(size),
      width: size[0],
      height: size[1],
    }
  }

  if (mapLoading) {
    recaptureOnLoadEnd = true
    return
  }
  store.planningCaptureGeneration = generation
}

const onMapLoadStart = () => {
  mapLoading = true
}

const onMapLoadEnd = () => {
  mapLoading = false
  if (recaptureOnLoadEnd) {
    recaptureOnLoadEnd = false
    exportMap()
  }
}

// A tile that failed and was retried by LayerService loads outside the map's
// loadstart/loadend bookkeeping; its arrival is the only signal that the
// capture taken with a hole in it is stale.
const recaptureAfterTiles = debounce(() => exportMap(), 300)

watch(
  () => store.planningRecaptureRequest,
  () => exportMap(),
)

// RED-293 self-healing overlay. The colored maakasutus tiles ride XGIS2
// service ids that rotate without notice, so the card never trusts a url
// baked into constants (the overlay entries carry none): once the map and
// the planning payload are both ready it applies the backend-resolved
// overlay (layer, url, LAYERS) to the OpenLayers source, and when that
// source's tiles still fail it asks the backend to verify + re-resolve and
// re-applies — no button, no reload. A budget of HEAL_BUDGET attempts per
// overlay identity (a healed url is a new identity) with a cooldown keeps a
// genuinely dead upstream from turning into a request loop.
const HEAL_BUDGET = 2
const HEAL_COOLDOWN_MS = 30_000
const mapReady = shallowRef(false)
let heals = { key: null, count: 0, lastAt: 0 }
let healingSource = null
const onSourceFailed = event => {
  if (event.key !== TILE_LOAD_FAILED_KEY || !healingSource?.get(TILE_LOAD_FAILED_KEY)) {
    return
  }
  const now = Date.now()
  if (heals.count >= HEAL_BUDGET || now - heals.lastAt < HEAL_COOLDOWN_MS) {
    return
  }
  heals.count += 1
  heals.lastAt = now
  store.fetchOverlaySource(planningInfo.value?.landUseSource, true).then(overlay => {
    if (overlay) {
      applyOverlay(overlay)
    }
  })
}

const overlayLayers = () => {
  const group = map.value?.layers?.info
  if (!group) {
    return []
  }
  return group
    .getLayers()
    .getArray()
    .filter(layer => DETAIL_PLANNINGS_OVERLAY_LAYER_IDS.includes(layer.get('id')))
}

const applyOverlay = overlay => {
  const layers = overlayLayers()
  if (!layers.length) {
    return
  }
  // Every overlay change (first apply, heal, or none for a raster-only
  // parcel) invalidates the capture the export would otherwise ship.
  store.planningMapGeneration += 1
  const identity = overlay ? `${overlay.layerId}|${overlay.url ?? ''}|${overlay.layers ?? ''}` : null
  if (heals.key !== identity) {
    heals = { key: identity, count: 0, lastAt: 0 }
  }
  for (const layer of layers) {
    const active = !!overlay && layer.get('id') === overlay.layerId
    const source = layer.getSource()
    layer.setVisible(active)
    if (!source) {
      continue
    }
    if (!active) {
      // A losing tier must never keep the map-wide notice up.
      resetTileRetry(source)
      continue
    }
    if (typeof source.setUrl === 'function' && overlay.url && source.getUrls?.()?.[0] !== overlay.url) {
      source.setUrl(overlay.url)
    }
    if (typeof source.updateParams === 'function' && overlay.layers) {
      source.updateParams({ LAYERS: overlay.layers })
    }
    if (healingSource !== source) {
      healingSource?.un('propertychange', onSourceFailed)
      healingSource?.un('tileloadend', recaptureAfterTiles)
      healingSource = source
      source.on('propertychange', onSourceFailed)
      source.on('tileloadend', recaptureAfterTiles)
    }
    resetTileRetry(source)
    source.refresh()
  }
  // The switched-on tier has to reach the export: waitForMapLoaded() renders
  // synchronously, so this capture waits for the tiles just requested.
  exportMap()
}

// RED-293 round 3: the legend floats over the map and follows the viewport
// (state / PLANK / Tallinn legends list only the categories in view; a
// raster plan's curated legend is fixed). Debounced on moveend; a response
// that is older than the latest request is dropped.
const planningLegend = shallowRef(null)
let legendRequest = 0
let legendTimer = null

const viewportBbox = () => {
  const olMap = map.value?.map
  const size = olMap?.getSize()
  if (!size?.[0] || !size?.[1]) {
    return null
  }
  return olMap.getView().calculateExtent(size)
}

const refreshLegend = async () => {
  const info = planningInfo.value
  const tier = info?.landUseSource
  if (!info?.found || !tier || !mapReady.value) {
    planningLegend.value = null
    return
  }
  const bbox = viewportBbox()
  if (!bbox) {
    return
  }
  const request = ++legendRequest
  const legend = await store.fetchPlanningLegend({
    tier,
    planid: info.plan?.planid ?? null,
    bbox,
    scale: PLANNING_LEGEND_RENDER_SCALE,
  })
  if (request === legendRequest) {
    planningLegend.value = legend
  }
}

const onMoveEnd = () => {
  if (planningInfo.value?.landUseSource === 'raster') {
    return
  }
  clearTimeout(legendTimer)
  legendTimer = setTimeout(refreshLegend, 600)
}

watch(
  [mapReady, () => planningInfo.value],
  ([ready, info]) => {
    if (ready && info) {
      applyOverlay(info.overlay ?? null)
      refreshLegend()
    }
  },
)

onUnmounted(() => {
  healingSource?.un('propertychange', onSourceFailed)
  healingSource?.un('tileloadend', recaptureAfterTiles)
  healingSource = null
  recaptureAfterTiles.cancel()
  for (const layer of overlayLayers()) {
    const source = layer.getSource()
    if (source) {
      resetTileRetry(source)
    }
  }
  clearTimeout(legendTimer)
  const olMap = map.value?.map
  olMap?.un('moveend', onMoveEnd)
  olMap?.un('loadstart', onMapLoadStart)
  olMap?.un('loadend', onMapLoadEnd)
  // No map left to capture from — whatever was captured is what the export
  // gets, so an unmount between apply and capture never holds it up.
  store.planningCaptureGeneration = store.planningMapGeneration
})

const onMapReady = () => {
  mapReady.value = true
  const olMap = map.value?.map
  olMap?.on('moveend', onMoveEnd)
  olMap?.on('loadstart', onMapLoadStart)
  olMap?.on('loadend', onMapLoadEnd)

  if (landBoard.value?.geometry) {
    createMapFeatures(landBoard.value.geometry)

    if (map.value) {
      map.value.updateOverlay(mapview.value.overlay)
    }
  }

  if (landBoard.value?.bbox && !mapview.value.bbox) {
    mapview.value.bbox = landBoard.value.bbox

    if (map.value) {
      map.value.updateBbox(landBoard.value.bbox)
    }
  }

  loading.value = false
  exportMap()
}
</script>

<style scoped>
  .plannings-map {
    position: relative;
    height: 600px;
    width: 100%;
    margin: 0;
  }
</style>
